// BlockNoteModal.js - Modal för blockering och anteckningar

import React, { useState, useEffect, useContext } from 'react';
import { createBlock, updateBlock, deleteBlock, getUserSchedule } from './calendarApi';
import { UserContext } from '../../UserContext';
import './BlockNoteModal.css';

const BlockNoteModal = ({
  mode = 'block', // 'block' eller 'note'
  initialDate,
  existingBlock = null,
  selectedUserId,
  clinicUsers = [],
  onClose,
  onSave
}) => {
  const { user: currentUser } = useContext(UserContext);

  // Form state
  const [title, setTitle] = useState('');
  const [startTime, setStartTime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [userId, setUserId] = useState('');
  const [recurrencePattern, setRecurrencePattern] = useState('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');

  // Block category (block, sick, vacation) - endast för mode='block'
  const [blockCategory, setBlockCategory] = useState('block');

  // Spår I1: blockerar tiden (ej bokningsbar) eller bara märkning?
  // Default: block/sick/vacation blockerar, anteckning är bara märkning.
  const [blocksBooking, setBlocksBooking] = useState(mode === 'block');

  // Schema för vald användare
  const [userSchedule, setUserSchedule] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sätt titel baserat på mode
  const isBlock = mode === 'block';
  const modalTitle = existingBlock
    ? (isBlock ? 'Redigera blockering' : 'Redigera anteckning')
    : (isBlock ? 'Blocka tid' : 'Lägg till anteckning');

  // Initiera formulär
  useEffect(() => {
    if (existingBlock) {
      setTitle(existingBlock.title || '');
      setStartTime(formatDateTimeLocal(new Date(existingBlock.start_time)));

      // Beräkna duration från start och slut
      const start = new Date(existingBlock.start_time);
      const end = new Date(existingBlock.end_time);
      const duration = Math.round((end - start) / (1000 * 60));
      setDurationMinutes(duration || 60);

      setUserId(existingBlock.user_id?.toString() || '');
      setRecurrencePattern(existingBlock.recurrence_pattern || 'none');
      setRecurrenceEndDate(existingBlock.recurrence_end_date || '');

      // Sätt block category från befintlig block_type
      const blockType = existingBlock.block_type || 'block';
      if (['sick', 'vacation', 'admin'].includes(blockType)) {
        setBlockCategory(blockType);
      } else if (blockType === 'block') {
        setBlockCategory('block');
      }

      // Bokningsbarhet: läs sparat val, fall tillbaka på typens gamla semantik
      setBlocksBooking(
        existingBlock.blocks_booking !== undefined && existingBlock.blocks_booking !== null
          ? !!existingBlock.blocks_booking
          : blockType !== 'note'
      );
    } else if (initialDate) {
      setStartTime(formatDateTimeLocal(initialDate));
      setUserId(selectedUserId || currentUser?.user_id?.toString() || '');

      // Föreslå rubrik baserat på mode
      if (isBlock) {
        setTitle('');
      } else {
        setTitle('');
      }
    }
  }, [existingBlock, initialDate, selectedUserId, currentUser, isBlock]);

  // Hämta schema för vald användare
  useEffect(() => {
    const fetchSchedule = async () => {
      if (!userId) return;
      try {
        const schedule = await getUserSchedule(parseInt(userId));
        setUserSchedule(schedule);
      } catch (err) {
        console.log('Kunde inte hämta schema:', err);
        setUserSchedule(null);
      }
    };
    fetchSchedule();
  }, [userId]);

  function formatDateTimeLocal(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  // Snabbval för duration
  const durationPresets = [
    { label: '15 min', value: 15 },
    { label: '30 min', value: 30 },
    { label: '45 min', value: 45 },
    { label: '1 tim', value: 60 },
    { label: '2 tim', value: 120 },
    { label: 'Heldag', value: 480 }
  ];

  // Block categories (typer av blockeringar)
  const blockCategories = [
    { value: 'block', label: 'Vanlig blockering', icon: 'fa-ban', color: '#f39c12' },
    { value: 'sick', label: 'Sjukdom', icon: 'fa-thermometer-half', color: '#e74c3c' },
    { value: 'vacation', label: 'Semester', icon: 'fa-umbrella-beach', color: '#3498db' },
    { value: 'admin', label: 'Administration', icon: 'fa-briefcase', color: '#95a5a6' }
  ];

  // Snabbval för vanliga blockeringar (beroende på kategori)
  const getBlockPresets = () => {
    if (blockCategory === 'sick') {
      return [
        { label: 'Heldag', title: 'Sjuk', duration: 480 },
        { label: 'Halvdag FM', title: 'Sjuk (förmiddag)', duration: 240 },
        { label: 'Halvdag EM', title: 'Sjuk (eftermiddag)', duration: 240 }
      ];
    }
    if (blockCategory === 'vacation') {
      return [
        { label: 'Heldag', title: 'Semester', duration: 480 },
        { label: 'Halvdag', title: 'Semester (halvdag)', duration: 240 }
      ];
    }
    if (blockCategory === 'admin') {
      return [
        { label: 'Administration', title: 'Administrativt', duration: 120 },
        { label: 'Kort admin', title: 'Administrativt', duration: 30 }
      ];
    }
    // Default block presets
    return [
      { label: 'Lunch', title: 'Lunch', duration: 60 },
      { label: 'Rast', title: 'Rast', duration: 15 },
      { label: 'Möte', title: 'Möte', duration: 60 }
    ];
  };

  const blockPresets = getBlockPresets();

  // Hjälpfunktion för att få veckodagsnamn från Date
  const getDayName = (date) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  // Hjälpfunktion för att hämta schematider för en specifik dag
  const getScheduleForDay = (date) => {
    if (!userSchedule || !userSchedule.schedule) return null;
    const dayName = getDayName(date);
    const daySchedule = userSchedule.schedule[dayName];
    if (!daySchedule || !daySchedule.working) return null;
    return daySchedule;
  };

  const handlePresetClick = (preset) => {
    setTitle(preset.title);

    // Specialhantering för "Heldag"
    if (preset.label === 'Heldag' || preset.label === 'Halvdag') {
      const selectedDate = startTime ? new Date(startTime) : new Date();
      const daySchedule = getScheduleForDay(selectedDate);

      if (daySchedule && daySchedule.start && daySchedule.end) {
        // Använd schematider
        const [startHour, startMin] = daySchedule.start.split(':').map(Number);
        const [endHour, endMin] = daySchedule.end.split(':').map(Number);

        // Beräkna total arbetstid i minuter
        const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);

        if (preset.label === 'Heldag') {
          // Sätt starttid till schemastart
          const newStart = new Date(selectedDate);
          newStart.setHours(startHour, startMin, 0, 0);
          setStartTime(formatDateTimeLocal(newStart));
          setDurationMinutes(totalMinutes);
        } else if (preset.label === 'Halvdag') {
          // Halvdag = halva arbetstiden
          setDurationMinutes(Math.round(totalMinutes / 2));
        }
      } else {
        // Inget schema - använd standardtider (07:00-17:00)
        if (preset.label === 'Heldag') {
          const newStart = new Date(selectedDate);
          newStart.setHours(7, 0, 0, 0);
          setStartTime(formatDateTimeLocal(newStart));
          setDurationMinutes(600); // 10 timmar
        } else {
          setDurationMinutes(300); // 5 timmar för halvdag
        }
      }
    } else if (preset.label === 'Halvdag FM') {
      // Förmiddag - använd schema eller standard
      const selectedDate = startTime ? new Date(startTime) : new Date();
      const daySchedule = getScheduleForDay(selectedDate);

      if (daySchedule && daySchedule.start) {
        const [startHour, startMin] = daySchedule.start.split(':').map(Number);
        const newStart = new Date(selectedDate);
        newStart.setHours(startHour, startMin, 0, 0);
        setStartTime(formatDateTimeLocal(newStart));
      } else {
        const newStart = new Date(selectedDate);
        newStart.setHours(7, 0, 0, 0);
        setStartTime(formatDateTimeLocal(newStart));
      }
      setDurationMinutes(preset.duration);
    } else if (preset.label === 'Halvdag EM') {
      // Eftermiddag - börja efter lunch (ca mitt på dagen)
      const selectedDate = startTime ? new Date(startTime) : new Date();
      const daySchedule = getScheduleForDay(selectedDate);

      if (daySchedule && daySchedule.start && daySchedule.end) {
        const [startHour, startMin] = daySchedule.start.split(':').map(Number);
        const [endHour, endMin] = daySchedule.end.split(':').map(Number);
        // Beräkna mittpunkt
        const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
        const midMinutes = startHour * 60 + startMin + Math.round(totalMinutes / 2);
        const midHour = Math.floor(midMinutes / 60);
        const midMin = midMinutes % 60;

        const newStart = new Date(selectedDate);
        newStart.setHours(midHour, midMin, 0, 0);
        setStartTime(formatDateTimeLocal(newStart));
      } else {
        const newStart = new Date(selectedDate);
        newStart.setHours(12, 0, 0, 0);
        setStartTime(formatDateTimeLocal(newStart));
      }
      setDurationMinutes(preset.duration);
    } else {
      setDurationMinutes(preset.duration);
    }
  };

  // Handler för duration-knapparna
  const handleDurationPresetClick = (preset) => {
    if (preset.label === 'Heldag') {
      const selectedDate = startTime ? new Date(startTime) : new Date();
      const daySchedule = getScheduleForDay(selectedDate);

      if (daySchedule && daySchedule.start && daySchedule.end) {
        const [startHour, startMin] = daySchedule.start.split(':').map(Number);
        const [endHour, endMin] = daySchedule.end.split(':').map(Number);
        const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);

        // Sätt starttid till schemastart
        const newStart = new Date(selectedDate);
        newStart.setHours(startHour, startMin, 0, 0);
        setStartTime(formatDateTimeLocal(newStart));
        setDurationMinutes(totalMinutes);
      } else {
        // Inget schema - använd standardtider (07:00-17:00)
        const newStart = new Date(selectedDate);
        newStart.setHours(7, 0, 0, 0);
        setStartTime(formatDateTimeLocal(newStart));
        setDurationMinutes(600);
      }
    } else {
      setDurationMinutes(preset.value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Rubrik krävs');
      return;
    }

    if (!startTime) {
      setError('Starttid krävs');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Använd datetime-local värdet direkt (utan UTC-konvertering)
      // Formatet är redan YYYY-MM-DDTHH:MM
      // För block mode, använd blockCategory (block/sick/vacation)
      // För note mode, använd 'note'
      const blockData = {
        block_type: isBlock ? blockCategory : 'note',
        blocks_booking: blocksBooking,
        title: title.trim(),
        start_time: startTime, // Skicka som lokal tid utan konvertering
        duration_minutes: durationMinutes,
        user_id: userId ? parseInt(userId) : currentUser?.user_id,
        recurrence_pattern: recurrencePattern,
        recurrence_end_date: recurrenceEndDate || null
      };

      if (existingBlock) {
        await updateBlock(existingBlock.block_id, blockData);
      } else {
        await createBlock(blockData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving block:', err);
      setError(err.response?.data?.error || 'Kunde inte spara');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!existingBlock) return;

    const hasRecurrence = existingBlock.recurrence_pattern !== 'none';
    let deleteSeries = false;

    if (hasRecurrence) {
      const choice = window.confirm(
        'Vill du ta bort hela serien? (OK = Ta bort alla, Avbryt = Ta bara bort denna)'
      );
      deleteSeries = choice;
    } else {
      if (!window.confirm('Är du säker på att du vill ta bort detta?')) {
        return;
      }
    }

    setLoading(true);
    try {
      await deleteBlock(existingBlock.block_id, deleteSeries);
      onSave();
    } catch (err) {
      console.error('Error deleting block:', err);
      setError('Kunde inte ta bort');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="block-note-modal" onClick={e => e.stopPropagation()}>
        <div className="bnm-header">
          <h3>
            <i className={`fas ${isBlock ? 'fa-ban' : 'fa-sticky-note'}`}></i>
            {modalTitle}
          </h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bnm-form">
          {error && <div className="bnm-error">{error}</div>}

          {/* Typ-indikator - texten följer valet av bokningsbarhet (I1) */}
          <div className={`bnm-type-badge ${mode} ${blockCategory}`}>
            <i className={`fas ${isBlock ? (blockCategories.find(c => c.value === blockCategory)?.icon || 'fa-ban') : 'fa-sticky-note'}`}></i>
            {blocksBooking ? 'Blockerar bokning på denna tid' : 'Endast märkning - bokning tillåten'}
          </div>

          {/* Blockera bokningar av/på (I1) */}
          <div className="bnm-field bnm-blocks-booking">
            <label className="bnm-checkbox-label">
              <input
                type="checkbox"
                checked={blocksBooking}
                onChange={e => setBlocksBooking(e.target.checked)}
              />
              <span>
                <i className="fas fa-ban"></i>
                Blockera bokningar på denna tid
              </span>
            </label>
            <span className="bnm-hint">
              {blocksBooking
                ? 'Tiden går inte att boka.'
                : 'Tiden förblir bokningsbar - t.ex. "öronmärkt för patient X" eller "boka endast knän".'}
            </span>
          </div>

          {/* Block-kategori väljare (endast för block mode) */}
          {isBlock && (
            <div className="bnm-category-section">
              <label className="bnm-section-label">
                <i className="fas fa-tag"></i>
                Typ av blockering
              </label>
              <div className="bnm-category-options">
                {blockCategories.map(cat => (
                  <label
                    key={cat.value}
                    className={`bnm-category-option ${blockCategory === cat.value ? 'active' : ''}`}
                    style={{ '--category-color': cat.color }}
                  >
                    <input
                      type="radio"
                      name="blockCategory"
                      value={cat.value}
                      checked={blockCategory === cat.value}
                      onChange={e => {
                        setBlockCategory(e.target.value);
                        // Rensa titel när kategori ändras så snabbval kan sätta ny
                        if (!existingBlock) setTitle('');
                      }}
                    />
                    <i className={`fas ${cat.icon}`}></i>
                    <span>{cat.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Snabbval (endast för block) */}
          {isBlock && (
            <div className="bnm-presets">
              <label>Snabbval:</label>
              <div className="bnm-preset-buttons">
                {blockPresets.map(preset => (
                  <button
                    key={preset.label}
                    type="button"
                    className="bnm-preset-btn"
                    onClick={() => handlePresetClick(preset)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Rubrik */}
          <div className="bnm-field">
            <label>
              <i className="fas fa-heading"></i>
              Rubrik *
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={isBlock ? 'T.ex. Lunch, Möte, Semester...' : 'T.ex. Kom ihåg att ringa patient...'}
              autoFocus
            />
          </div>

          {/* Tid och varaktighet */}
          <div className="bnm-row">
            <div className="bnm-field">
              <label>
                <i className="fas fa-clock"></i>
                Starttid *
              </label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
              />
            </div>

            <div className="bnm-field">
              <label>
                <i className="fas fa-hourglass-half"></i>
                Varaktighet
              </label>
              <div className="bnm-duration-input">
                <input
                  type="number"
                  min="5"
                  max="1440"
                  step="5"
                  value={durationMinutes}
                  onChange={e => setDurationMinutes(parseInt(e.target.value) || 60)}
                />
                <span>minuter</span>
              </div>
              <div className="bnm-duration-presets">
                {durationPresets.map(preset => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`bnm-duration-btn ${durationMinutes === preset.value ? 'active' : ''}`}
                    onClick={() => handleDurationPresetClick(preset)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Behandlare */}
          <div className="bnm-field">
            <label>
              <i className="fas fa-user-md"></i>
              Gäller för
            </label>
            <select value={userId} onChange={e => setUserId(e.target.value)}>
              {clinicUsers.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Upprepning */}
          <div className="bnm-recurrence-section">
            <label className="bnm-section-label">
              <i className="fas fa-redo"></i>
              Upprepning
            </label>

            <div className="bnm-recurrence-options">
              <label className={`bnm-radio ${recurrencePattern === 'none' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="recurrence"
                  value="none"
                  checked={recurrencePattern === 'none'}
                  onChange={e => setRecurrencePattern(e.target.value)}
                />
                <span>Ingen upprepning</span>
              </label>

              <label className={`bnm-radio ${recurrencePattern === 'daily' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="recurrence"
                  value="daily"
                  checked={recurrencePattern === 'daily'}
                  onChange={e => setRecurrencePattern(e.target.value)}
                />
                <span>Varje dag</span>
              </label>

              <label className={`bnm-radio ${recurrencePattern === 'weekly' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="recurrence"
                  value="weekly"
                  checked={recurrencePattern === 'weekly'}
                  onChange={e => setRecurrencePattern(e.target.value)}
                />
                <span>Varje vecka</span>
              </label>

              <label className={`bnm-radio ${recurrencePattern === 'biweekly' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="recurrence"
                  value="biweekly"
                  checked={recurrencePattern === 'biweekly'}
                  onChange={e => setRecurrencePattern(e.target.value)}
                />
                <span>Varannan vecka</span>
              </label>
            </div>

            {recurrencePattern !== 'none' && (
              <div className="bnm-recurrence-end">
                <label>
                  <i className="fas fa-calendar-times"></i>
                  Upprepa till och med:
                </label>
                <input
                  type="date"
                  value={recurrenceEndDate}
                  onChange={e => setRecurrenceEndDate(e.target.value)}
                  min={startTime ? startTime.split('T')[0] : ''}
                />
                <span className="bnm-hint">
                  Lämna tomt för att upprepa tills vidare
                </span>
              </div>
            )}
          </div>

          {/* Knappar */}
          <div className="bnm-actions">
            {existingBlock && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={loading}
              >
                <i className="fas fa-trash"></i>
                Ta bort
              </button>
            )}

            <div className="bnm-actions-right">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Avbryt
              </button>
              <button
                type="submit"
                className={`btn ${isBlock ? 'btn-warning' : 'btn-primary'}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Sparar...
                  </>
                ) : (
                  <>
                    <i className={`fas ${isBlock ? 'fa-ban' : 'fa-sticky-note'}`}></i>
                    {existingBlock ? 'Uppdatera' : 'Spara'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlockNoteModal;
